import React, { useState } from 'react';
import {
  Card,
  Tabs,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  App,
  Tag,
  Space,
  Typography,
} from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ownerIntelligenceService from '@/services/ownerIntelligence.service';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { OiPageFrame, OiEmptyState } from '@/components/owner-intelligence/OwnerIntelligenceComponents';

const ExecutiveActionCenterPage: React.FC = () => {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [actionModal, setActionModal] = useState(false);
  const [decisionModal, setDecisionModal] = useState(false);
  const [thresholdModal, setThresholdModal] = useState(false);
  const [thresholdForm] = Form.useForm();
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') ?? 'alerts';
  const [actionForm] = Form.useForm();
  const [decisionForm] = Form.useForm();

  const { data: alerts, isLoading: alertsLoading } = useQuery({
    queryKey: ['owner-intelligence', 'alerts'],
    queryFn: () => ownerIntelligenceService.listAlerts('open'),
  });
  const { data: actions, isLoading: actionsLoading } = useQuery({
    queryKey: ['owner-intelligence', 'actions'],
    queryFn: () => ownerIntelligenceService.listActions(),
  });
  const { data: decisions, isLoading: decisionsLoading } = useQuery({
    queryKey: ['owner-intelligence', 'decisions'],
    queryFn: () => ownerIntelligenceService.listDecisions(),
  });
  const { data: thresholds } = useQuery({
    queryKey: ['owner-intelligence', 'alert-thresholds'],
    queryFn: () => ownerIntelligenceService.listAlertThresholds(),
  });
  const { data: accessPolicies } = useQuery({
    queryKey: ['owner-intelligence', 'access-policies'],
    queryFn: () => ownerIntelligenceService.listAccessPolicies(),
  });
  const { data: myAccess } = useQuery({
    queryKey: ['owner-intelligence', 'my-access'],
    queryFn: () => ownerIntelligenceService.getMyAccessPolicy(),
  });
  const [policyUserId, setPolicyUserId] = useState('');
  const [policyRole, setPolicyRole] = useState('department_head');
  const [commentModal, setCommentModal] = useState<{ actionId: string } | null>(null);
  const [commentText, setCommentText] = useState('');
  const [assignModal, setAssignModal] = useState<{ actionId: string } | null>(null);
  const [assignUserId, setAssignUserId] = useState('');

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['owner-intelligence', 'alerts'] });
    qc.invalidateQueries({ queryKey: ['owner-intelligence', 'actions'] });
    qc.invalidateQueries({ queryKey: ['owner-intelligence', 'decisions'] });
  };

  const resolveAlert = async (id: string) => {
    try {
      await ownerIntelligenceService.resolveAlert(id);
      message.success('Alert resolved');
      invalidate();
    } catch (e: unknown) {
      message.error((e as Error).message);
    }
  };

  const createAction = async () => {
    try {
      const values = await actionForm.validateFields();
      await ownerIntelligenceService.createAction({
        ...values,
        due_date: values.due_date?.format('YYYY-MM-DD'),
      });
      message.success('Action created');
      setActionModal(false);
      actionForm.resetFields();
      invalidate();
    } catch (e: unknown) {
      message.error((e as Error).message);
    }
  };

  const updateAction = async (id: string, status: string) => {
    try {
      await ownerIntelligenceService.updateActionStatus(id, { status });
      message.success('Action updated');
      invalidate();
    } catch (e: unknown) {
      message.error((e as Error).message);
    }
  };

  const createDecision = async () => {
    try {
      const values = await decisionForm.validateFields();
      await ownerIntelligenceService.createDecision({
        ...values,
        decision_date: values.decision_date?.format('YYYY-MM-DD'),
      });
      message.success('Decision logged');
      setDecisionModal(false);
      decisionForm.resetFields();
      invalidate();
    } catch (e: unknown) {
      message.error((e as Error).message);
    }
  };

  const submitComment = async () => {
    if (!commentModal) return;
    try {
      await ownerIntelligenceService.addActionComment(commentModal.actionId, commentText);
      message.success('Comment added');
      setCommentModal(null);
      setCommentText('');
    } catch (e: unknown) {
      message.error((e as Error).message);
    }
  };

  const submitAssign = async () => {
    if (!assignModal || !assignUserId) return;
    try {
      await ownerIntelligenceService.assignAction(assignModal.actionId, assignUserId);
      message.success('Action assigned');
      setAssignModal(null);
      setAssignUserId('');
      invalidate();
    } catch (e: unknown) {
      message.error((e as Error).message);
    }
  };

  const saveThreshold = async () => {
    try {
      const values = await thresholdForm.validateFields();
      await ownerIntelligenceService.upsertAlertThreshold(values);
      message.success('Threshold saved');
      setThresholdModal(false);
      thresholdForm.resetFields();
      qc.invalidateQueries({ queryKey: ['owner-intelligence', 'alert-thresholds'] });
    } catch (e: unknown) {
      message.error((e as Error).message);
    }
  };

  const saveAccessPolicy = async () => {
    if (!policyUserId) {
      message.warning('Enter a user ID for the policy');
      return;
    }
    try {
      await ownerIntelligenceService.upsertAccessPolicy({
        user_id: policyUserId,
        role_label: policyRole,
        access_scope: policyRole.includes('department') ? 'department' : policyRole.includes('division') ? 'division' : 'company',
        mask_financial: policyRole.includes('department'),
        mask_customer_names: policyRole.includes('department'),
      });
      message.success('Access policy saved');
      setPolicyUserId('');
      qc.invalidateQueries({ queryKey: ['owner-intelligence', 'access-policies'] });
    } catch (e: unknown) {
      message.error((e as Error).message);
    }
  };

  return (
    <OiPageFrame subtitle="Executive alerts, action tracking, and decision log — synced from risk snapshots on refresh.">
      <Tabs
        defaultActiveKey={activeTab}
        items={[
          {
            key: 'alerts',
            label: `Alerts (${(alerts as any[])?.length ?? 0})`,
            children: (
              <Card>
                <Table
                  size="small"
                  rowKey="id"
                  loading={alertsLoading}
                  dataSource={(alerts as any[]) ?? []}
                  locale={{
                    emptyText: (
                      <OiEmptyState
                        variant="positive"
                        title="No open alerts"
                        body="Risk snapshots are clear — refresh from Command Center to sync new exceptions."
                      />
                    ),
                  }}
                  columns={[
                    { title: 'Title', dataIndex: 'title' },
                    { title: 'Description', dataIndex: 'description', ellipsis: true },
                    {
                      title: 'Severity',
                      dataIndex: 'severity',
                      render: (v: string) => (
                        <Tag color={v === 'critical' ? 'red' : v === 'warning' ? 'orange' : 'blue'}>{v}</Tag>
                      ),
                    },
                    { title: 'Count', dataIndex: 'actual_value' },
                    {
                      title: '',
                      key: 'actions',
                      render: (_: unknown, row: { id: string }) => (
                        <Button size="small" onClick={() => resolveAlert(row.id)}>
                          Resolve
                        </Button>
                      ),
                    },
                  ]}
                />
              </Card>
            ),
          },
          {
            key: 'actions',
            label: `Actions (${(actions as any[])?.length ?? 0})`,
            children: (
              <Card
                extra={
                  <Button type="primary" onClick={() => setActionModal(true)}>
                    New action
                  </Button>
                }
              >
                <Table
                  size="small"
                  rowKey="id"
                  loading={actionsLoading}
                  dataSource={(actions as any[]) ?? []}
                  locale={{
                    emptyText: (
                      <OiEmptyState
                        variant="positive"
                        title="No open actions"
                        body="All alerts have been resolved."
                        actionLabel="New action"
                        onAction={() => setActionModal(true)}
                      />
                    ),
                  }}
                  columns={[
                    { title: 'Title', dataIndex: 'title' },
                    { title: 'Priority', dataIndex: 'priority' },
                    { title: 'Due', dataIndex: 'due_date' },
                    { title: 'Status', dataIndex: 'status' },
                    {
                      title: '',
                      key: 'update',
                      render: (_: unknown, row: { id: string; status: string }) => (
                        <Space wrap>
                          {row.status !== 'completed' && (
                            <>
                              <Button size="small" onClick={() => updateAction(row.id, 'in_progress')}>Start</Button>
                              <Button size="small" type="primary" onClick={() => updateAction(row.id, 'completed')}>Complete</Button>
                              <Button size="small" onClick={() => setAssignModal({ actionId: row.id })}>Assign</Button>
                              <Button
                                size="small"
                                onClick={() => {
                                  const uid = (row as { owner_user_id?: string }).owner_user_id;
                                  if (!uid) {
                                    message.warning('Assign an owner before escalating');
                                    return;
                                  }
                                  ownerIntelligenceService.escalateAction(row.id, uid).then(invalidate);
                                }}
                              >
                                Escalate
                              </Button>
                            </>
                          )}
                          {(row.status === 'completed' || row.status === 'closed') && (
                            <Button size="small" onClick={() => ownerIntelligenceService.reopenAction(row.id).then(invalidate)}>Reopen</Button>
                          )}
                          <Button size="small" onClick={() => setCommentModal({ actionId: row.id })}>Comment</Button>
                        </Space>
                      ),
                    },
                  ]}
                />
              </Card>
            ),
          },
          {
            key: 'decisions',
            label: `Decisions (${(decisions as any[])?.length ?? 0})`,
            children: (
              <Card
                extra={
                  <Button type="primary" onClick={() => setDecisionModal(true)}>
                    Log decision
                  </Button>
                }
              >
                <Table
                  size="small"
                  rowKey="id"
                  loading={decisionsLoading}
                  dataSource={(decisions as any[]) ?? []}
                  columns={[
                    { title: 'Decision', dataIndex: 'decision_title' },
                    { title: 'Category', dataIndex: 'decision_category' },
                    { title: 'Date', dataIndex: 'decision_date' },
                    { title: 'Reason', dataIndex: 'reason', ellipsis: true },
                  ]}
                />
              </Card>
            ),
          },
          {
            key: 'thresholds',
            label: 'Alert thresholds',
            children: (
              <Card extra={<Button type="primary" size="small" onClick={() => setThresholdModal(true)}>Add threshold</Button>}>
                <Table size="small" rowKey="id" dataSource={(thresholds as any[]) ?? []} columns={[
                  { title: 'KPI', dataIndex: 'kpi_code' },
                  { title: 'Name', dataIndex: 'threshold_name' },
                  { title: 'Op', dataIndex: 'comparison_op' },
                  { title: 'Value', dataIndex: 'threshold_value' },
                  { title: 'Severity', dataIndex: 'severity' },
                ]} />
              </Card>
            ),
          },
          {
            key: 'governance',
            label: 'Governance',
            children: (
              <Card title="Your access">
                <Typography.Paragraph>
                  Level: <Tag>{(myAccess as { level?: string })?.level ?? 'director'}</Tag>
                  Scope: <Tag>{(myAccess as { scope?: string })?.scope ?? 'company'}</Tag>
                  {(myAccess as { maskFinancial?: boolean })?.maskFinancial && <Tag color="orange">Financial masked</Tag>}
                </Typography.Paragraph>
                <Typography.Title level={5}>User access policies</Typography.Title>
                <Space style={{ marginBottom: 12 }} wrap>
                  <Input placeholder="User ID" value={policyUserId} onChange={(e) => setPolicyUserId(e.target.value)} style={{ width: 220 }} />
                  <Select value={policyRole} onChange={setPolicyRole} style={{ width: 180 }} options={[
                    { value: 'owner', label: 'Owner' },
                    { value: 'director', label: 'Director' },
                    { value: 'division_head', label: 'Division head' },
                    { value: 'department_head', label: 'Department head' },
                  ]} />
                  <Button type="primary" onClick={saveAccessPolicy}>Save policy</Button>
                </Space>
                <Table size="small" rowKey="id" dataSource={(accessPolicies as any[]) ?? []} columns={[
                  { title: 'User', dataIndex: 'user_id' },
                  { title: 'Role', dataIndex: 'role_label' },
                  { title: 'Scope', dataIndex: 'access_scope' },
                  { title: 'Mask $', dataIndex: 'mask_financial', render: (v: number) => (Number(v) ? 'Yes' : 'No') },
                  { title: 'Mask names', dataIndex: 'mask_customer_names', render: (v: number) => (Number(v) ? 'Yes' : 'No') },
                ]} />
              </Card>
            ),
          },
        ]}
      />

      <Modal title="Add comment" open={!!commentModal} onCancel={() => setCommentModal(null)} onOk={submitComment}>
        <Input.TextArea rows={3} value={commentText} onChange={(e) => setCommentText(e.target.value)} />
      </Modal>
      <Modal title="Assign action" open={!!assignModal} onCancel={() => setAssignModal(null)} onOk={submitAssign}>
        <Input placeholder="Owner user ID" value={assignUserId} onChange={(e) => setAssignUserId(e.target.value)} />
      </Modal>

      <Modal title="New executive action" open={actionModal} onCancel={() => setActionModal(false)} onOk={createAction}>
        <Form form={actionForm} layout="vertical">
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="priority" label="Priority" initialValue="medium">
            <Select options={[{ value: 'high' }, { value: 'medium' }, { value: 'low' }]} />
          </Form.Item>
          <Form.Item name="due_date" label="Due date">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="Log decision" open={decisionModal} onCancel={() => setDecisionModal(false)} onOk={createDecision}>
        <Form form={decisionForm} layout="vertical" initialValues={{ decision_date: dayjs() }}>
          <Form.Item name="decision_title" label="Decision" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="decision_category" label="Category">
            <Input placeholder="e.g. pricing, hiring, capex" />
          </Form.Item>
          <Form.Item name="reason" label="Reason">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="decision_date" label="Date">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="Alert threshold" open={thresholdModal} onCancel={() => setThresholdModal(false)} onOk={saveThreshold}>
        <Form form={thresholdForm} layout="vertical" initialValues={{ comparison_op: 'gt', severity: 'warning' }}>
          <Form.Item name="kpi_code" label="KPI code" rules={[{ required: true }]}>
            <Input placeholder="revenue_mtd" />
          </Form.Item>
          <Form.Item name="threshold_name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="comparison_op" label="Operator">
            <Select options={[{ value: 'gt', label: '>' }, { value: 'lt', label: '<' }]} />
          </Form.Item>
          <Form.Item name="threshold_value" label="Value" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="severity" label="Severity">
            <Select options={[{ value: 'critical', label: 'Critical' }, { value: 'warning', label: 'Warning' }]} />
          </Form.Item>
        </Form>
      </Modal>
    </OiPageFrame>
  );
};

export default ExecutiveActionCenterPage;
